import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/joy";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ListIcon from "@mui/icons-material/List";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import GroupIcon from "@mui/icons-material/Group";
import { Database } from "../../lib/database";
import { useAuthStore } from "../../store/authStore";
import { useSavedListStore } from "../../store/savedListStore";
import { SaveListButton } from "../SaveListButton";

type List = Database["public"]["Tables"]["lists"]["Row"];

export interface ListWithPlaceCount extends List {
  place_count: number;
}

interface ProfileListsProps {
  lists: ListWithPlaceCount[];
  loading: boolean;
  onListClick: (listId: string) => void;
  expandedListId: string | null;
  isOwnProfile?: boolean;
}

const getPrivacyIcon = (privacyLevel: string | null) => {
  switch (privacyLevel) {
    case "public":
      return <PublicIcon fontSize="small" aria-label="public" />;
    case "private":
      return <LockIcon fontSize="small" aria-label="private" />;
    case "friends":
      return <GroupIcon fontSize="small" aria-label="friends" />;
    default:
      return <PublicIcon fontSize="small" aria-label="public" />;
  }
};

const getPlaceCountText = (count: number): string => {
  if (count === 0) return "No places";
  if (count === 1) return "1 place";
  return `${count} places`;
};

export const ProfileLists = ({
  lists,
  loading,
  onListClick,
  expandedListId,
  isOwnProfile = true,
}: ProfileListsProps) => {
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");
  const { profile } = useAuthStore();
  const { fetchSavedListIds } = useSavedListStore();

  React.useEffect(() => {
    if (!isOwnProfile && profile?.id) {
      fetchSavedListIds(profile.id);
    }
  }, [isOwnProfile, profile?.id, fetchSavedListIds]);

  // Filter to show only public lists when viewing other profiles
  const displayLists = isOwnProfile
    ? lists
    : lists.filter((list) => list.privacy_level === "public");

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (displayLists.length === 0) {
    return (
      <Card variant="outlined" sx={{ padding: 4, textAlign: "center" }}>
        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
          {isOwnProfile
            ? "No lists yet. Create your first travel list!"
            : "No public lists to display."}
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <Typography level="h4">
          {isOwnProfile ? "Your Lists" : "Lists"}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            variant={viewMode === "grid" ? "solid" : "outlined"}
            size="sm"
            onClick={() => setViewMode("grid")}
            sx={{ minWidth: "auto", px: 1 }}
            aria-label="grid view"
          >
            <ViewModuleIcon fontSize="small" />
          </Button>
          <Button
            variant={viewMode === "list" ? "solid" : "outlined"}
            size="sm"
            onClick={() => setViewMode("list")}
            sx={{ minWidth: "auto", px: 1 }}
            aria-label="list view"
          >
            <ListIcon fontSize="small" />
          </Button>
        </Stack>
      </Stack>

      <Box
        data-testid="profile-lists-container"
        data-view-mode={viewMode}
        sx={{
          display: viewMode === "grid" ? "grid" : "flex",
          gridTemplateColumns: viewMode === "grid" ? "repeat(2, 1fr)" : undefined,
          flexDirection: viewMode === "list" ? "column" : undefined,
          gap: 2,
        }}
      >
        {displayLists.map((list) => {
          const isExpanded = expandedListId === list.id;
          return (
            <Card
              key={list.id}
              variant="outlined"
              data-expanded={isExpanded}
              onClick={() => onListClick(list.id)}
              sx={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: isExpanded ? "2px solid" : "1px solid",
                borderColor: isExpanded ? "primary.500" : "divider",
                "&:hover": {
                  borderColor: "primary.300",
                  boxShadow: "sm",
                },
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Typography level="title-md" sx={{ fontWeight: "bold" }}>
                    {list.title}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {!isOwnProfile && <SaveListButton listId={list.id} />}
                    {getPrivacyIcon(list.privacy_level)}
                  </Stack>
                </Stack>

                {list.description && (
                  <Typography
                    level="body-sm"
                    sx={{
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      marginTop: 0.5,
                    }}
                  >
                    {list.description}
                  </Typography>
                )}

                <Typography
                  level="body-xs"
                  sx={{ color: "text.tertiary", marginTop: 1 }}
                >
                  {getPlaceCountText(list.place_count)}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};
